import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  CreditCard, ShieldCheck, Loader2, ArrowLeft, RefreshCw, Trash2,
  AlertTriangle, Plus, ClipboardList, Check, X,
} from "lucide-react";
import { toast } from "sonner";

const BRAND_LABEL = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express",
  discover: "Discover", jcb: "JCB", diners: "Diners", unionpay: "UnionPay",
};

function fmtBrand(b) {
  if (!b) return "Card";
  return BRAND_LABEL[String(b).toLowerCase()]
      || String(b).charAt(0).toUpperCase() + String(b).slice(1);
}

function fmtEvent(evt) {
  return {
    added: { label: "Card added", tone: "emerald", Icon: Plus },
    updated: { label: "Card updated", tone: "sky", Icon: RefreshCw },
    reauthorized: { label: "Card re-authorized", tone: "sky", Icon: ShieldCheck },
    removed: { label: "Card removed", tone: "rose", Icon: Trash2 },
  }[evt] || { label: evt || "Event", tone: "slate", Icon: ClipboardList };
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * PaymentMethods — dedicated Customer Portal page for managing the saved
 * card + AutoPay + card audit log. iter175.
 */
export default function PaymentMethods() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: h }] = await Promise.all([
        api.get("/me/saved-card"),
        api.get("/me/card-history"),
      ]);
      setCard(c);
      setHistory(h?.history || []);
    } catch {
      setCard(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  // If URL has ?card_saved=1&session_id=..., call setup-status to persist
  useEffect(() => {
    const url = new URL(window.location.href);
    const cs = url.searchParams.get("card_saved");
    const sid = url.searchParams.get("session_id");
    if (cs === "1" && sid) {
      (async () => {
        try {
          const { data } = await api.get(`/me/payment-method/setup-status/${sid}`);
          if (data.saved) {
            toast.success(`Card saved — ${fmtBrand(data.brand)} ending ${data.last4}`);
            refresh();
          }
        } catch {
          toast.error("Could not confirm saved card — please retry");
        } finally {
          url.searchParams.delete("card_saved");
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
        }
      })();
    }
  }, [refresh]);

  const startStripeSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/payment-method/setup-session", {});
      if (data.setup_url) {
        window.location.href = data.setup_url;
      } else {
        toast.error("Could not start card setup");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Card setup failed");
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const { data } = await api.delete("/me/saved-card");
      if (data.removed) {
        const flipped = data.future_visits_flipped || 0;
        if (flipped) {
          toast.success(`Card removed — ${flipped} recurring visit${flipped === 1 ? "" : "s"} now show 'Payment Method Required'`);
        } else {
          toast.success("Payment method removed");
        }
        refresh();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    } finally {
      setBusy(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#047857]" />
      </div>
    );
  }

  const hasCard = !!card?.has_card;
  const autopayActive = !!card?.autopay_enabled && !card?.autopay_admin_disabled && hasCard;
  const pmRequired = !!card?.payment_method_required;
  const brand = fmtBrand(card?.brand);
  const last4 = card?.last4 || "••••";
  const exp = card?.exp_month && card?.exp_year
      ? `${String(card.exp_month).padStart(2, "0")}/${String(card.exp_year).slice(-2)}`
      : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F6F8F5] to-white pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        <Link
          to="/profile"
          className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-gray-900"
          data-testid="pm-back-to-profile"
        >
          <ArrowLeft className="w-4 h-4" /> Back to profile
        </Link>

        <div className="mt-4 flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#062818] text-white flex items-center justify-center flex-shrink-0">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#047857] font-extrabold">Customer portal</div>
            <h1 className="mt-0.5 text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]"
                data-testid="pm-page-title">
              Payment Methods
            </h1>
            <p className="mt-1 text-sm text-gray-600 max-w-xl">
              Manage the card we use for AutoPay on your recurring services.
              Removing your card never cancels a booking — it just pauses
              automatic billing until you add a new one.
            </p>
          </div>
        </div>

        {loading && (
          <div className="mt-10 flex items-center justify-center py-16 rounded-3xl border border-gray-200 bg-white">
            <Loader2 className="w-7 h-7 animate-spin text-[#047857]" />
          </div>
        )}

        {!loading && (
          <>
            {/* Card panel */}
            <section
              className="mt-6 rounded-3xl bg-white border border-gray-200 shadow-sm overflow-hidden"
              data-testid="payment-method-panel"
            >
              {hasCard ? (
                <div className="p-5 sm:p-7">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#062818] to-[#0F3F26] text-white flex items-center justify-center">
                        <CreditCard className="w-7 h-7" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
                          Saved payment method
                        </div>
                        <div className="text-xl sm:text-2xl font-extrabold text-gray-900 font-[Outfit]"
                             data-testid="pm-card-brand-last4">
                          {brand} •••• {last4}
                        </div>
                        <div className="text-xs text-gray-600 mt-0.5" data-testid="pm-card-exp">
                          {exp ? `Expires ${exp}` : "Saved securely with Stripe"}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.14em] font-extrabold ${autopayActive
                        ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                        : "bg-amber-100 text-amber-800 ring-1 ring-amber-200"}`}
                      data-testid="pm-autopay-badge"
                    >
                      {autopayActive ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                      AutoPay {autopayActive ? "active" : "paused"}
                    </span>
                  </div>

                  {pmRequired && (
                    <div
                      className="mt-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-2"
                      data-testid="pm-required-banner"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-900">
                        <div className="font-extrabold">Payment method required</div>
                        <div className="mt-0.5 text-xs">
                          Your recurring visits are still scheduled but paused
                          for AutoPay. Add a new card to resume automatic
                          billing.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Button
                      onClick={startStripeSetup}
                      disabled={busy}
                      className="h-12 rounded-full bg-[#062818] hover:bg-[#0F3F26] text-white font-extrabold"
                      data-testid="pm-update-card-btn"
                    >
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                      Update Card
                    </Button>
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={busy}
                      variant="outline"
                      className="h-12 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50 font-extrabold"
                      data-testid="pm-remove-card-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Remove Card
                    </Button>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500">
                    <ShieldCheck className="w-3.5 h-3.5" /> PCI-compliant · Stripe · Cancel anytime
                  </div>
                </div>
              ) : (
                <div className="p-6 sm:p-10 text-center" data-testid="pm-empty-state">
                  <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <CreditCard className="w-7 h-7" />
                  </div>
                  <h2 className="mt-3 text-lg font-extrabold text-gray-900 font-[Outfit]">
                    No payment method on file
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 max-w-md mx-auto">
                    Add a card to enable AutoPay. We only charge after each
                    completed visit — never before.
                  </p>
                  {pmRequired && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-900 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em]"
                         data-testid="pm-required-inline">
                      <AlertTriangle className="w-3.5 h-3.5" /> Payment method required
                    </div>
                  )}
                  <Button
                    onClick={startStripeSetup}
                    disabled={busy}
                    className="mt-5 rounded-full bg-[#047857] hover:bg-[#065F46] text-white h-12 px-6 font-extrabold"
                    data-testid="pm-add-card-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-1.5" />}
                    Add payment method
                  </Button>
                </div>
              )}
            </section>

            {/* Audit log */}
            <section className="mt-6 rounded-3xl bg-white border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Audit log</div>
                  <div className="text-base font-extrabold text-gray-900 font-[Outfit]">
                    Payment method activity
                  </div>
                </div>
              </div>
              {history.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500" data-testid="pm-history-empty">
                  No card activity yet.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100" data-testid="pm-history-list">
                  {history.map((h, idx) => {
                    const meta = fmtEvent(h.event);
                    const Icon = meta.Icon;
                    const toneBg = { emerald: "bg-emerald-100 text-emerald-700", sky: "bg-sky-100 text-sky-700", rose: "bg-rose-100 text-rose-700", slate: "bg-slate-100 text-slate-700" }[meta.tone];
                    return (
                      <li key={idx} className="p-4 sm:p-5 flex items-start gap-3" data-testid={`pm-history-${h.event}`}>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${toneBg}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-extrabold text-gray-900 text-sm">{meta.label}</span>
                            {h.brand && h.last4 && (
                              <span className="text-xs font-bold text-gray-500">
                                {fmtBrand(h.brand)} •••• {h.last4}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 text-[12px] text-gray-600">
                            {fmtWhen(h.at)}
                            {h.actor && (
                              <> · by <span className="font-bold capitalize">{h.actor}</span>
                                {h.by && h.by !== h.actor && ` (${h.by})`}
                              </>
                            )}
                          </div>
                          {h.reason && (
                            <div className="mt-1 text-[11px] uppercase tracking-[0.12em] font-bold text-gray-400">
                              {h.reason.replace(/_/g, " ")}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      {/* Confirmation dialog with the EXACT wording */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="pm-remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove payment method?</AlertDialogTitle>
            <AlertDialogDescription data-testid="pm-remove-confirm-text">
              Removing your payment method will disable AutoPay for all
              future recurring services until you add a new payment method.
              Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pm-remove-cancel">Keep card</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="pm-remove-confirm"
            >
              Remove card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
